/**
 * What the front of a card says, line by line: the one place that turns a
 * `CardProfile` into the face's text. The card on screen (business-card.tsx),
 * the image the Share button sends (card-image.ts) and the print export
 * (print-package.ts) all read it, so they cannot drift apart.
 */

import { formatCityLine } from "@/lib/address";
import type { CardProfile } from "@/lib/api";
import { formatPhone } from "@/lib/phone";

export interface CardFace {
  name: string;
  jobTitle: string | null;
  /**
   * The company line: the legal entity from Entra (`companyName`,
   * "StrikoWestofen GmbH"), the brand only when Entra has none. It is the
   * backend's `org_name`, the same value as the vCard ORG, so the printed
   * line and the scanned contact agree.
   */
  company: string;
  /** The e-mail split at its "@" so a line can break there first; ["", ""] without an e-mail. */
  email: [local: string, domain: string];
  /** The mobile number, else the office number; the prefix is "M:" or "T:". */
  phone: { prefix: "M:" | "T:"; value: string } | null;
  /** Street lines as Entra holds them (may contain line breaks), trimmed. */
  street: string | null;
  cityLine: string;
  country: string | null;
  /** "norican.com": the website without scheme, "www." and trailing slash. */
  website: string;
}

export function cardFace(card: CardProfile): CardFace {
  return {
    name: card.display_name,
    jobTitle: card.job_title?.trim() || null,
    company: card.org_name,
    email: splitEmail(card.email),
    phone: card.mobile_phone
      ? { prefix: "M:", value: formatPhone(card.mobile_phone) }
      : card.business_phone
        ? { prefix: "T:", value: formatPhone(card.business_phone) }
        : null,
    street: card.street_address?.trim() || null,
    cityLine: formatCityLine(card),
    country: card.country?.trim() || null,
    website: card.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, ""),
  };
}

/**
 * The text that goes with the shared card image: the face's contact lines,
 * so a messenger or mail client shows them as tappable numbers and links
 * next to the picture. Both numbers are listed here, the face shows one.
 */
export function shareText(card: CardProfile): string {
  const face = cardFace(card);
  const [local, domain] = face.email;
  const lines = [
    face.name,
    face.jobTitle ? `${face.jobTitle}, ${face.company}` : face.company,
    card.mobile_phone ? `M: ${formatPhone(card.mobile_phone)}` : null,
    card.business_phone ? `T: ${formatPhone(card.business_phone)}` : null,
    domain ? `${local}@${domain}` : local || null,
    face.website,
  ];
  return lines.filter(Boolean).join("\n");
}

/**
 * The e-mail split at its "@" so the card can offer a line break there
 * before it has to break anywhere else; an address without an "@" is kept
 * whole.
 */
function splitEmail(email: string | null | undefined): [string, string] {
  const value = email?.trim() ?? "";
  const at = value.indexOf("@");
  return at > 0 ? [value.slice(0, at), value.slice(at + 1)] : [value, ""];
}
